import type { ProfileDto } from '../common/dtos';
import { FetchError } from 'ofetch';
import { defineStore } from 'pinia';
import type { ActivateAlternateDto } from '../common/dtos/activate-alternate.dto';

async function refreshAuthSession(): Promise<boolean> {
  try {
    const { getSession } = useAuth();
    await getSession({ force: true });
    return true;
  } catch {
    return false;
  }
}

async function reportLoginSecuritySave(successMessage: string, refreshFailureMessage: string): Promise<void> {
  const { $toastSuccess, $toastError } = useNuxtApp();
  const refreshed = await refreshAuthSession();
  if (!refreshed) {
    $toastError(refreshFailureMessage);
    return;
  }

  $toastSuccess(successMessage);
}

interface ProfileState {
  preview: string;
  loading: boolean;
  allTokens: string[];
  isVerifyPhoneToastVisible: boolean;
}

export const useProfileStore = defineStore('profileStore', {
  state: (): ProfileState => ({
    preview: '',
    loading: false,
    allTokens: [],
    isVerifyPhoneToastVisible: false,
  }),
  actions: {
    async updateProfileImage(firstName: string, lastName: string, businessName: string, preview: string, file: File) {
      const { $snapApi } = useNuxtApp();

      const rawData = {
        firstName,
        lastName,
        businessName,
      };

      const rawDataJSON = JSON.stringify(rawData);

      const formData = new FormData();

      if (file) {
        formData.append('image', file, file.name);
      }

      formData.append('data', rawDataJSON);

      this.loading = true;

      await $snapApi({
        method: 'put',
        url: 'profile',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      this.loading = false;
      this.preview = preview || '/images/avatar.png';
    },
    async createNewKey(name: string, isPrimary: boolean) {
      const { $snapApi } = useNuxtApp();

      await $snapApi({
        method: 'post',
        url: 'client-api-key/create-new',
        data: {
          name,
          isPrimary,
        },
      });
    },
    async updateKey(id: number, name: string, isPrimary: boolean) {
      const { $snapApi } = useNuxtApp();

      await $snapApi({
        method: 'post',
        url: 'client-api-key/update',
        data: {
          id,
          name,
          isPrimary,
        },
      });
    },
    async createProfile(user: ProfileDto, isAgent: boolean, isSender: boolean = false) {
      const { $snapApi } = useNuxtApp();
      this.loading = true;

      await $snapApi({
        method: 'post',
        url: `profile${isSender ? '/sender' : ''}?isAgentSnap=${isAgent}`,
        data: user,
      });

      this.loading = false;
    },
    async authorizeUser(
      credentials: Pick<ProfileDto, 'email' | 'password'>,
      params?: { callbackUrl?: string; redirect?: boolean },
    ) {
      const { token, data } = useAuthState();
      const { signIn } = useAuth();
      const { $toastError } = useNuxtApp();

      this.loading = true;

      localStorage.removeItem('_snap_token');
      localStorage.removeItem('_profile_uid');
      localStorage.removeItem('_owner_id');
      localStorage.removeItem('_user_roles');
      localStorage.removeItem('_origin_app');
      localStorage.removeItem('snap_is_agent');

      try {
        await signIn(
          {
            email: credentials.email,
            password: credentials.password,
          },
          params ?? { callbackUrl: '/' },
        );
        if (token.value && data.value) {
          localStorage.setItem('_snap_token', token.value.split(' ')[1]);
          localStorage.setItem('_profile_uid', data.value.id.toString());
          localStorage.setItem('_owner_id', data.value.profileId.toString());
          localStorage.setItem('_user_roles', JSON.stringify(data.value.roles));

          return { success: true };
        }
      } catch (error) {
        if (error instanceof FetchError) {
          // Check if email is not verified
          if (error.data?.message === 'EMAIL_NOT_VERIFIED') {
            navigateTo(`/email-verification-required?email=${encodeURIComponent(credentials.email)}`);
            return;
          }
          $toastError(error.data.message);
        }
      } finally {
        this.loading = false;
      }
    },
    async resetPassword(token: string, password: string) {
      const { $snapApi } = useNuxtApp();

      this.loading = true;
      const data = {
        token,
        newPassword: password,
      };
      await $snapApi({ method: 'post', url: '/unsecured/reset-password', data });
      this.loading = false;
    },
    async updateNotifyEmail(isAllowed: boolean) {
      const { $snapApi, $toastSuccess } = useNuxtApp();

      this.loading = true;
      try {
        await $snapApi({ method: 'put', url: 'profile/payment-email-preference', data: { isAllowed } });

        $toastSuccess('Notification email preference updated successfully');
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async updateMarketingEmail(isAllowed: boolean) {
      const { $snapApi, $toastSuccess } = useNuxtApp();

      this.loading = true;
      try {
        await $snapApi({ method: 'put', url: 'profile/marketing-email-preference', data: { isAllowed } });

        $toastSuccess('Marketing email preference updated successfully');
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async updateNotifyText(isAllowed: boolean) {
      const { $snapApi, $toastSuccess } = useNuxtApp();

      this.loading = true;
      try {
        await $snapApi({ method: 'put', url: 'profile/payment-sms-preference', data: { isAllowed } });

        $toastSuccess('SMS preference updated successfully');
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async updateLoginSecurityEmail(isAllowed: boolean): Promise<boolean> {
      const { $snapApi, $toastError } = useNuxtApp();

      this.loading = true;
      try {
        await $snapApi({ method: 'put', url: 'profile-info/login-security-email-preference', data: { isAllowed } });
        await reportLoginSecuritySave(
          'Login security email preference updated successfully',
          'Login security email preference was saved, but could not be refreshed. Reload this page.',
        );
        return true;
      } catch (error) {
        if (error instanceof FetchError) {
          $toastError(error.data?.message || 'Failed to update login security email preference');
        } else {
          $toastError('Failed to update login security email preference');
        }
        return false;
      } finally {
        this.loading = false;
      }
    },
    async updateLoginSecuritySms(isAllowed: boolean): Promise<boolean> {
      const { $snapApi, $toastError } = useNuxtApp();

      this.loading = true;
      try {
        await $snapApi({ method: 'put', url: 'profile-info/login-security-sms-preference', data: { isAllowed } });
        await reportLoginSecuritySave(
          'Login security text preference updated successfully',
          'Login security text preference was saved, but could not be refreshed. Reload this page.',
        );
        return true;
      } catch (error) {
        if (error instanceof FetchError) {
          $toastError(error.data?.message || 'Failed to update login security text preference');
        } else {
          $toastError('Failed to update login security text preference');
        }
        return false;
      } finally {
        this.loading = false;
      }
    },
    async updateLoginMfa(channel: 'email' | 'sms'): Promise<boolean> {
      const { $snapApi, $toastError } = useNuxtApp();

      this.loading = true;
      try {
        await $snapApi({
          method: 'put',
          url: 'profile-info/login-mfa-preference',
          data: { channel },
        });
        await reportLoginSecuritySave(
          'Login verification code preference updated successfully',
          'Login verification code preference was saved, but could not be refreshed. Reload this page.',
        );
        return true;
      } catch (error) {
        if (error instanceof FetchError) {
          $toastError(error.data?.message || 'Failed to update login verification code preference');
        } else {
          $toastError('Failed to update login verification code preference');
        }
        return false;
      } finally {
        this.loading = false;
      }
    },
    async updateIdleTimeout(minutes: number): Promise<boolean> {
      const { $snapApi, $toastError } = useNuxtApp();

      this.loading = true;
      try {
        await $snapApi({ method: 'put', url: 'profile-info/idle-timeout-preference', data: { minutes } });
        await reportLoginSecuritySave(
          'Inactivity timeout updated successfully',
          'Inactivity timeout was saved, but could not be refreshed. Reload this page.',
        );
        return true;
      } catch (error) {
        if (error instanceof FetchError) {
          $toastError(error.data?.message || 'Failed to update inactivity timeout');
        } else {
          $toastError('Failed to update inactivity timeout');
        }
        return false;
      } finally {
        this.loading = false;
      }
    },
    async updateShowSenderEmailOnPaymentDetails(isAllowed: boolean): Promise<boolean> {
      const { $snapApi, $toastSuccess, $toastError } = useNuxtApp();

      this.loading = true;
      try {
        await $snapApi({ method: 'put', url: 'profile-info/show-sender-email-preference', data: { isAllowed } });

        $toastSuccess('Payment details email preference updated successfully');
        return true;
      } catch (error) {
        if (error instanceof FetchError) {
          $toastError(error.data?.message || 'Failed to update payment details email preference');
        } else {
          $toastError('Failed to update payment details email preference');
        }
        return false;
      } finally {
        this.loading = false;
      }
    },
    async sendVerificationCode() {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      try {
        await $snapApi({ method: 'put', url: 'profile-info/send-verification-code' });
      } finally {
        this.loading = false;
      }
    },
    async verifyPhone(phoneCode: string) {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      try {
        await $snapApi({ method: 'put', url: 'profile-info/verify-phone', data: { phoneCode } });
      } finally {
        this.loading = false;
      }
    },
    async updatePhone(phone: string) {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      try {
        await $snapApi({ method: 'put', url: 'profile-info/update-phone', data: { phone } });
      } finally {
        this.loading = false;
      }
    },
    async signOut() {
      // Redirect to logout page first

      await navigateTo('/logout');
    },
    async activateAlternatePlatform(dto: ActivateAlternateDto) {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      try {
        await $snapApi({ method: 'post', url: 'profile/activate-alternate-platform', data: dto });
      } catch {
      } finally {
        this.loading = false;
      }
    },
  },
});
